import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isSafeMode } from '../lib/safeMode';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VideoCard } from '../components/VideoCard';
import { SecuritySettingsCard } from '../components/SecuritySettingsCard';
import { Heart, Eye, Wallet, ArrowDownToLine, Trash2, BarChart3, MessageCircle, Users, Pencil, CreditCard, ShieldCheck, Download, Clock3, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../i18n';
import { translateCategory } from '../lib/categories';

const formatWatchTime = (seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours) return `${hours} h ${minutes} min`;
    if (minutes) return `${minutes} min ${secs} s`;
    return `${secs} s`;
};

// IAB-style banner sizes accepted for external promo ads
const STANDARD_BANNER_SIZES = [[300, 250], [336, 280], [728, 90], [970, 250], [468, 60], [320, 50], [160, 600], [1200, 628]];

export default function ProfilePage() {
    const { user, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const { t, locale } = useI18n();
    const [mine, setMine] = useState([]);
    const [history, setHistory] = useState([]);
    const [recos, setRecos] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [categories, setCategories] = useState([]);
    const [editingVideo, setEditingVideo] = useState(null);
    // Subtítols dels vídeos propis (editor)
    const [subtitleLangs, setSubtitleLangs] = useState([]);
    const [subLang, setSubLang] = useState('ca');
    const [subFile, setSubFile] = useState(null);
    const [subText, setSubText] = useState('');
    const [subMode, setSubMode] = useState('file');
    const [subBusy, setSubBusy] = useState(false);
    const [editingProfile, setEditingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({ name: '', bio: '', picture: null, instagram: '', tiktok: '', twitter: '', youtube: '' });
    const [social, setSocial] = useState({ followers: [], following: [] });
    const [receivedComments, setReceivedComments] = useState([]);
    const [replyDrafts, setReplyDrafts] = useState({});
    const [blockedUsers, setBlockedUsers] = useState([]);
    const [adultBusy, setAdultBusy] = useState(false);
    const [birthdate, setBirthdate] = useState('');
    const [adultPasswordPrompt, setAdultPasswordPrompt] = useState(false);
    const [adultPassword, setAdultPassword] = useState('');
    const [amount, setAmount] = useState('');
    const [analyticsDays, setAnalyticsDays] = useState(30);
    const [activeTab, setActiveTab] = useState('analytics');
    // Self-serve promo ads (paid from the wallet)
    const [mySeries, setMySeries] = useState([]);
    const [promoConfig, setPromoConfig] = useState(null);
    const [promoAds, setPromoAds] = useState([]);
    const [promoForm, setPromoForm] = useState({ kind: 'video', target_video_id: '', target_series_id: '', target_url: '', title: '', placement: 'banner', views: '' });
    const [promoBusy, setPromoBusy] = useState(false);
    const [walletPacks, setWalletPacks] = useState([]);
    const [topupBusy, setTopupBusy] = useState(false);
    const [promoBannerFile, setPromoBannerFile] = useState(null);
    const [connectStatus, setConnectStatus] = useState(null);
    const [pinValue, setPinValue] = useState('');
    const [pinValue2, setPinValue2] = useState('');
    const [pinPassword, setPinPassword] = useState('');
    const [connectBusy, setConnectBusy] = useState(false);

    useEffect(() => {
        if (loading) return; // Wait for auth to finish loading
        if (!user) { navigate('/entrar'); return; }
        (async () => {
            // Use allSettled so that a single failing request (e.g. right after
            // verifying the birthdate/age, which changes `user` and re-triggers
            // this effect) doesn't wipe out the wallet, views and other sections
            // that already loaded successfully.
            api.get('/subtitle-languages').then((r) => setSubtitleLangs(r.data.languages || [])).catch(() => {});
            const [m, h, r, w, a, c, socialResponse, commentsResponse, blockedResponse, seriesResponse, promoCfgResponse, promoAdsResponse, packsResponse, connectResponse] = await Promise.allSettled([
                api.get('/users/me/videos'),
                api.get('/users/me/history'),
                api.get('/users/me/recommendations'),
                api.get('/users/me/wallet'),
                api.get('/users/me/analytics', { params: { days: analyticsDays } }),
                api.get('/categories'),
                api.get('/users/me/social'),
                api.get('/users/me/comments'),
                api.get('/users/me/blocked-users'),
                api.get('/series/mine'),
                api.get('/promo/config'),
                api.get('/promo/ads'),
                api.get('/wallet/packs'),
                api.get('/stripe-connect/status'),
            ]);
            if (m.status === 'fulfilled') setMine(m.value.data.items || []);
            if (h.status === 'fulfilled') setHistory(h.value.data.items || []);
            if (r.status === 'fulfilled') setRecos(r.value.data.items || []);
            if (w.status === 'fulfilled') setWallet(w.value.data);
            if (a.status === 'fulfilled') setAnalytics(a.value.data);
            if (c.status === 'fulfilled') setCategories(c.value.data.categories || []);
            if (socialResponse.status === 'fulfilled') setSocial(socialResponse.value.data || { followers: [], following: [] });
            if (commentsResponse.status === 'fulfilled') setReceivedComments(commentsResponse.value.data.items || []);
            if (blockedResponse.status === 'fulfilled') setBlockedUsers(blockedResponse.value.data.items || []);
            if (seriesResponse.status === 'fulfilled') setMySeries(seriesResponse.value.data.items || []);
            if (promoCfgResponse.status === 'fulfilled') {
                setPromoConfig(promoCfgResponse.value.data);
                setPromoForm((previous) => ({ ...previous, views: previous.views || String(promoCfgResponse.value.data.min_views || 100) }));
            }
            if (promoAdsResponse.status === 'fulfilled') setPromoAds(promoAdsResponse.value.data.items || []);
            if (packsResponse.status === 'fulfilled') setWalletPacks(packsResponse.value.data.packs || []);
            if (connectResponse.status === 'fulfilled') setConnectStatus(connectResponse.value.data);
            const failed = [m, h, r, w, a, c, socialResponse, commentsResponse, blockedResponse].filter((res) => res.status === 'rejected');
            if (failed.length) {
                console.error('No s\u2019han pogut carregar algunes dades del perfil', failed.map((res) => res.reason));
            }
            setProfileForm({ name: user.name || '', bio: user.bio || '', picture: null, instagram: user.social_links?.instagram || '', tiktok: user.social_links?.tiktok || '', twitter: user.social_links?.twitter || '', youtube: user.social_links?.youtube || '' });
        })();
    }, [user, loading, navigate, analyticsDays]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('topup') === 'success') {
            toast.success('Pagament rebut: el saldo s’afegirà a la cartera en uns segons');
            window.history.replaceState({}, '', '/perfil');
            setTimeout(async () => {
                try {
                    const w = await api.get('/users/me/wallet');
                    setWallet(w.data);
                } catch (e) { /* el webhook de Stripe encara pot estar processant */ }
            }, 8000);
        }
        const stripeParam = params.get('stripe');
        if (stripeParam === 'connected' || stripeParam === 'reauth') {
            toast.success(stripeParam === 'connected' ? 'Compte Stripe connectat: les retirades aprovades es pagaran automàticament' : 'Completa el registre del compte Stripe per poder cobrar');
            window.history.replaceState({}, '', '/perfil');
            (async () => {
                try { const s = await api.get('/stripe-connect/status'); setConnectStatus(s.data); } catch {}
            })();
        }
    }, []);

    const connectStripe = async () => {
        setConnectBusy(true);
        try {
            const { data } = await api.post('/stripe-connect/onboard');
            if (data.url) { window.location.href = data.url; return; }
            toast.success('Compte connectat (mode proves)');
            const s = await api.get('/stripe-connect/status');
            setConnectStatus(s.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "No s'ha pogut connectar amb Stripe");
        } finally {
            setConnectBusy(false);
        }
    };

    const submitWithdraw = async (e) => {
        e.preventDefault();
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error('Quantitat no vàlida'); return; }
        try {
            await api.post('/users/me/wallet/withdraw', { amount: amt });
            toast.success('Sol·licitud creada');
            const w = await api.get('/users/me/wallet');
            setWallet(w.data);
            refresh();
            setAmount('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        }
    };

    const downloadEarningsCsv = async () => {
        try {
            const r = await api.get('/users/me/earnings/csv', { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'albambam-ingressos-personals.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            toast.error('No s\u2019ha pogut descarregar el CSV');
        }
    };

    const saveMoneyPin = async (e) => {
        e.preventDefault();
        if (pinValue.length !== 6 || pinValue !== pinValue2) { toast.error('El PIN ha de ser de 6 xifres i els dos camps han de coincidir'); return; }
        if (!pinPassword) { toast.error('Introdueix la teva contrasenya'); return; }
        try {
            await api.post('/users/me/wallet/money-pin', { pin: pinValue, password: pinPassword });
            toast.success('PIN de seguretat desat');
            setPinValue('');
            setPinValue2('');
            setPinPassword('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No s\u2019ha pogut desar el PIN');
        }
    };

    const saveAutoWithdraw = async (enabled, intervalDays) => {
        try {
            await api.patch('/users/me/wallet/auto-withdraw', { enabled, interval_days: intervalDays });
            const w = await api.get('/users/me/wallet');
            setWallet(w.data);
            toast.success(enabled ? `Retirada automàtica activada: cada ${intervalDays} dies` : 'Retirada automàtica desactivada');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No s\u2019ha pogut desar la configuració');
        }
    };

    const deleteMyVideo = async (id) => {
        if (!confirm('Eliminar aquest vídeo?')) return;
        await api.delete(`/videos/${id}`);
        setMine((arr) => arr.filter((v) => v.video_id !== id));
    };

    const buyWalletPack = async (pack) => {
        setTopupBusy(true);
        try {
            const { data } = await api.post('/wallet/packs/checkout', { index: pack.index });
            if (data.url) { window.location.href = data.url; return; }
            toast.success('Recàrrega afegida a la cartera');
            const w = await api.get('/users/me/wallet');
            setWallet(w.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "No s'ha pogut iniciar el pagament");
        } finally {
            setTopupBusy(false);
        }
    };

    const promoteMyVideo = (video) => {
        setPromoForm((previous) => ({ ...previous, kind: 'video', target_video_id: video.video_id, target_series_id: '', target_url: '', title: '' }));
        setActiveTab('promo');
        document.getElementById('profile-tabs')?.scrollIntoView({ behavior: 'smooth' });
    };

    const validateBannerSize = (file, onValid) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const ok = STANDARD_BANNER_SIZES.some(([w, h]) => img.width === w && img.height === h);
            if (!ok) {
                toast.error(`Mida no estàndard (${img.width}x${img.height}). Acceptades: ${STANDARD_BANNER_SIZES.map(([w, h]) => `${w}x${h}`).join(', ')}`);
                return;
            }
            onValid();
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); toast.error('No es pot llegir la imatge'); };
        img.src = objectUrl;
    };

    const onPromoBannerFile = (file) => {
        if (!file) { setPromoBannerFile(null); return; }
        if (!file.type.startsWith('image/')) { toast.error('El bàner ha de ser una imatge'); return; }
        validateBannerSize(file, () => setPromoBannerFile(file));
    };

    const uploadPromoBanner = async (ad, file) => {
        try {
            const form = new FormData();
            form.append('file', file);
            await api.post(`/promo/ads/${ad.ad_id}/banner`, form);
            toast.success('Bàner actualitzat');
            await refreshPromoAndWallet();
        } catch (err) {
            toast.error(err.response?.data?.detail || "No s'ha pogut pujar el bàner");
        }
    };

    const refreshPromoAndWallet = async () => {
        const [ads, walletResponse] = await Promise.all([api.get('/promo/ads'), api.get('/users/me/wallet')]);
        setPromoAds(ads.data.items || []);
        setWallet(walletResponse.data);
    };

    const createPromoAd = async (event) => {
        event.preventDefault();
        setPromoBusy(true);
        try {
            const payload = { kind: promoForm.kind, placement: promoForm.placement, views: parseInt(promoForm.views || '0', 10) };
            if (promoForm.kind === 'video') payload.target_video_id = promoForm.target_video_id;
            if (promoForm.kind === 'series') payload.target_series_id = promoForm.target_series_id;
            if (promoForm.kind === 'external') { payload.target_url = promoForm.target_url; payload.title = promoForm.title; }
            const created = await api.post('/promo/ads', payload);
            if (promoForm.kind === 'external' && promoBannerFile) {
                try {
                    const form = new FormData();
                    form.append('file', promoBannerFile);
                    await api.post(`/promo/ads/${created.data.ad_id}/banner`, form);
                    setPromoBannerFile(null);
                } catch (bannerError) {
                    toast.error(`Promoció creada, però el bàner no s'ha pujat: ${bannerError.response?.data?.detail || 'error'}`);
                }
            }
            toast.success('Promoció creada: pagada amb la cartera');
            setPromoForm({ ...promoForm, target_video_id: '', target_series_id: '', target_url: '', title: '' });
            await refreshPromoAndWallet();
        } catch (error) {
            toast.error(error.response?.data?.detail || "No s'ha pogut crear la promoció");
        } finally {
            setPromoBusy(false);
        }
    };

    const togglePromoPause = async (ad) => {
        try {
            await api.post(`/promo/ads/${ad.ad_id}/${ad.paused ? 'resume' : 'pause'}`);
            const r = await api.get('/promo/ads');
            setPromoAds(r.data.items || []);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    const deletePromoAd = async (ad) => {
        if (!confirm('Vols esborrar aquesta promoció? Les visualitzacions no servides es retornen a la cartera.')) return;
        try {
            const r = await api.delete(`/promo/ads/${ad.ad_id}`);
            if (r.data.refunded > 0) toast.success(`Retornats ${r.data.refunded} ${promoConfig?.currency || 'EUR'} a la cartera`);
            await refreshPromoAndWallet();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    const setCommentsEnabled = async (videoId, enabled) => {
        try {
            const response = await api.patch(`/users/me/videos/${videoId}/settings`, { comments_enabled: enabled });
            setMine((items) => items.map((video) => video.video_id === videoId ? response.data : video));
            toast.success(enabled ? 'Comentaris activats' : 'Comentaris desactivats');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut desar');
        }
    };

    const saveVideo = async (event) => {
        event.preventDefault();
        try {
            const response = await api.patch(`/users/me/videos/${editingVideo.video_id}/settings`, {
                title: editingVideo.title,
                description: editingVideo.description,
                category: editingVideo.category,
                categories: editingVideo.categories && editingVideo.categories.length ? editingVideo.categories : [editingVideo.category],
                is_short: editingVideo.is_short,
                is_premium: editingVideo.is_premium,
                price: editingVideo.is_premium ? Number(editingVideo.price) : null,
                status: editingVideo.status,
                comments_enabled: editingVideo.comments_enabled,
            });
            setMine((items) => items.map((video) => video.video_id === editingVideo.video_id ? response.data : video));
            setEditingVideo(null);
            toast.success('Vídeo actualitzat');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el vídeo');
        }
    };

    // Subtítols: aplica la llista nova al diàleg i a la llista de vídeos
    const applySubtitles = (videoId, subtitles) => {
        setEditingVideo((prev) => (prev && prev.video_id === videoId ? { ...prev, subtitles } : prev));
        setMine((items) => items.map((video) => video.video_id === videoId ? { ...video, subtitles } : video));
    };

    const uploadSubtitle = async () => {
        if (!editingVideo) return;
        if (subMode === 'file' && !subFile) { toast.error('Tria un fitxer de subtítols (SRT o VTT)'); return; }
        if (subMode === 'text' && !subText.trim()) { toast.error('Enganxa o escriu el text dels subtítols'); return; }
        setSubBusy(true);
        try {
            const fd = new FormData();
            fd.append('language', subLang);
            if (subMode === 'file') {
                fd.append('file', subFile);
            } else {
                fd.append('file', new Blob([subText], { type: 'text/plain' }), 'subtitles.srt');
            }
            const r = await api.post(`/users/me/videos/${editingVideo.video_id}/subtitles`, fd);
            applySubtitles(editingVideo.video_id, r.data.subtitles || []);
            setSubFile(null);
            setSubText('');
            toast.success('Subtítols desats');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’han pogut desar els subtítols');
        } finally {
            setSubBusy(false);
        }
    };

    const deleteSubtitle = async (language) => {
        if (!editingVideo) return;
        try {
            await api.delete(`/users/me/videos/${editingVideo.video_id}/subtitles/${language}`);
            applySubtitles(editingVideo.video_id, (editingVideo.subtitles || []).filter((s) => s.lang !== language));
            toast.success('Subtítols eliminats');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’han pogut eliminar els subtítols');
        }
    };

    const saveProfile = async (event) => {
        event.preventDefault();
        try {
            await api.patch('/users/me', {
                name: profileForm.name,
                bio: profileForm.bio,
                instagram: profileForm.instagram,
                tiktok: profileForm.tiktok,
                twitter: profileForm.twitter,
                youtube: profileForm.youtube,
            });
            if (profileForm.picture) {
                const formData = new FormData();
                formData.append('file', profileForm.picture);
                await api.post('/users/me/picture', formData);
            }
            await refresh();
            setEditingProfile(false);
            toast.success('Perfil actualitzat');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el perfil');
        }
    };

    const replyToComment = async (commentId) => {
        const content = (replyDrafts[commentId] || '').trim();
        if (!content) return;
        try {
            const response = await api.post(`/comments/${commentId}/reply`, { content });
            setReceivedComments((items) => items.map((item) => item.comment_id === commentId
                ? { ...item, replies: [...(item.replies || []), response.data] }
                : item));
            setReplyDrafts((drafts) => ({ ...drafts, [commentId]: '' }));
            toast.success('Resposta publicada');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut publicar la resposta');
        }
    };

    const toggleBlockedUser = async (person) => {
        const blocked = blockedUsers.some((item) => item.user_id === person.user_id);
        try {
            if (blocked) {
                await api.delete(`/users/${person.user_id}/block`);
                setBlockedUsers((items) => items.filter((item) => item.user_id !== person.user_id));
                toast.success('Usuari desbloquejat');
            } else {
                await api.post(`/users/${person.user_id}/block`);
                setBlockedUsers((items) => [...items, person]);
                toast.success('Usuari bloquejat');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el bloqueig');
        }
    };

    const toggleCommentPermission = async (comment) => {
        const video = mine.find((item) => item.video_id === comment.video_id);
        if (!video) return;
        const allowed = video.comment_allowed_user_ids || [];
        const next = allowed.includes(comment.user_id)
            ? allowed.filter((id) => id !== comment.user_id)
            : [...allowed, comment.user_id];
        try {
            const response = await api.patch(`/users/me/videos/${comment.video_id}/settings`, { comment_allowed_user_ids: next });
            setMine((items) => items.map((item) => item.video_id === comment.video_id ? response.data : item));
            toast.success(next.includes(comment.user_id) ? 'Usuari autoritzat per comentar' : 'Usuari retirat de la llista');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el permís');
        }
    };

    const downloadCreatorAnalytics = () => {
        if (!analytics) return;
        const rows = [
            { tipus: 'resum', dies: analytics.period_days, visualitzacions: analytics.summary.views, guanys_visualitzacions: analytics.summary.view_revenue, guanys_vendes: analytics.summary.sales_revenue },
            ...(analytics.daily || []).map((item) => ({ tipus: 'dia', ...item })),
            ...(analytics.sales_ranking || []).map((item) => ({ tipus: 'venda', ...item })),
        ];
        const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
        const escape = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
        const csv = [columns.join(','), ...rows.map((row) => columns.map((column) => escape(row[column])).join(','))].join('\n');
        const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a'); link.href = url; link.download = `albambam-meva-analitica-${analytics.period_days}d.csv`; link.click(); URL.revokeObjectURL(url);
    };

    const setAdultContentEnabled = async (enabled) => {
        if (enabled && user.auth_provider !== 'google' && !adultPasswordPrompt) {
            setAdultPasswordPrompt(true);
            return;
        }
        setAdultBusy(true);
        try {
            await api.patch('/users/me', { adult_content_enabled: enabled, current_password: enabled ? adultPassword : undefined });
            await refresh();
            setAdultPasswordPrompt(false);
            setAdultPassword('');
            toast.success(enabled ? 'Contingut +18 activat' : 'Contingut +18 desactivat');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar la preferència');
        } finally {
            setAdultBusy(false);
        }
    };

    const confirmAdultPassword = async (event) => {
        event.preventDefault();
        await setAdultContentEnabled(true);
    };

    const subscribe = async () => {
        try {
            const response = await api.post('/users/me/subscription/checkout');
            if (response.data.url) {
                window.location.href = response.data.url;
                return;
            }
            await refresh();
            toast.success('Subscripció activada durant 30 dies');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut iniciar la subscripció');
        }
    };

    const verifyBirthdate = async (event) => {
        event.preventDefault();
        try {
            await api.patch('/users/me', { birthdate });
            await refresh();
            toast.success('Data de naixement desada');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Data no vàlida');
        }
    };

    if (!user) return null;
    return (
        <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 md:py-10 pb-24 md:pb-12" data-testid="profile-page">
            {/* Profile header */}
            <section className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                <Avatar className="h-16 w-16 ring-2 ring-[color:var(--alb-sun)]">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)] text-2xl">{(user.name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                    <h1 className="font-display text-2xl font-semibold" data-testid="profile-name">{user.name}</h1>
                    <div className="text-sm text-[color:var(--alb-text-muted)]">{user.email}</div>
                    {user.bio && <p className="mt-2 max-w-2xl whitespace-pre-wrap text-sm text-[color:var(--alb-text)]">{user.bio}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        <span className="inline-flex items-center gap-1 text-[color:var(--alb-pink)] font-mono-tab" data-testid="profile-total-likes"><Heart className="h-4 w-4 fill-[color:var(--alb-pink)]" /> {user.total_likes} M'agrades</span>
                        <span className="inline-flex items-center gap-1 text-[color:var(--alb-aqua)] font-mono-tab" data-testid="profile-total-views"><Eye className="h-4 w-4" /> {user.total_views} visualitzacions</span>
                        <button type="button" onClick={() => setActiveTab('social')} data-testid="profile-followers-button" className="inline-flex items-center gap-1 font-mono-tab hover:underline"><Users className="h-4 w-4" /> {social.followers.length} seguidors</button>
                        <button type="button" onClick={() => setActiveTab('social')} data-testid="profile-following-button" className="font-mono-tab hover:underline">{social.following.length} seguits</button>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate(`/usuari/${user.user_id}`)}><Eye className="mr-2 h-4 w-4" /> Veure perfil públic</Button>
                    <Button variant="outline" onClick={() => { setProfileForm({ name: user.name || '', bio: user.bio || '', picture: null, instagram: user.social_links?.instagram || '', tiktok: user.social_links?.tiktok || '', twitter: user.social_links?.twitter || '', youtube: user.social_links?.youtube || '' }); setEditingProfile(true); }}><Pencil className="mr-2 h-4 w-4" /> Editar perfil</Button>
                    <Button onClick={() => navigate('/pujar')} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">Pujar vídeo</Button>
                </div>
            </section>

            {/* Security / Change password */}
            <SecuritySettingsCard />

            {!isSafeMode() && (
            <Card className="p-5 mb-6 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="adult-access-card">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                        <h2 className="flex items-center gap-2 font-display font-semibold"><ShieldCheck className="h-5 w-5 text-[color:var(--alb-sun)]" /> Accés a contingut +18</h2>
                        <p className="mt-1 text-sm text-[color:var(--alb-text-muted)]">Cal ser major d’edat, tenir una subscripció activa i activar aquesta preferència.</p>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                            <span className={`rounded px-2 py-1 ${user.age >= 18 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>{user.age >= 18 ? 'Edat verificada' : 'Cal verificar que tens 18 anys'}</span>
                            <span className={`rounded px-2 py-1 ${user.subscription_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-200'}`}>{user.subscription_active ? `Subscripció activa${user.subscription_expires_at ? ` fins al ${new Date(user.subscription_expires_at).toLocaleDateString('ca')}` : ''}` : 'Sense subscripció'}</span>
                        </div>
                        {user.age == null && <form onSubmit={verifyBirthdate} className="mt-3 flex max-w-sm gap-2"><Input type="date" required value={birthdate} onChange={(event) => setBirthdate(event.target.value)} className="bg-[color:var(--alb-ink-2)]" /><Button type="submit" variant="outline">Verificar edat</Button></form>}
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        {!user.subscription_active && <Button onClick={subscribe} variant="outline" className="border-[color:var(--alb-hairline)]"><CreditCard className="mr-2 h-4 w-4" /> Subscriure’m</Button>}
                        <Label className="flex items-center justify-between gap-3 rounded border border-[color:var(--alb-hairline)] px-3 py-2">
                            Mostrar +18
                            <Switch checked={Boolean(user.adult_content_enabled)} disabled={adultBusy || user.age < 18} onCheckedChange={setAdultContentEnabled} data-testid="adult-content-toggle" />
                        </Label>
                    </div>
                </div>
                {adultPasswordPrompt && (
                    <form onSubmit={confirmAdultPassword} className="mt-3 flex max-w-sm flex-wrap items-center gap-2" data-testid="adult-content-password-confirm">
                        <Input type="password" required autoFocus placeholder="Confirma la teva contrasenya" value={adultPassword} onChange={(event) => setAdultPassword(event.target.value)} className="bg-[color:var(--alb-ink-2)]" />
                        <Button type="submit" disabled={adultBusy} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Confirmar</Button>
                        <Button type="button" variant="outline" onClick={() => { setAdultPasswordPrompt(false); setAdultPassword(''); }}>Cancel·lar</Button>
                    </form>
                )}
            </Card>
            )}

            {/* Wallet */}
            {wallet && (
                <Card data-testid="creator-wallet" className="p-5 mb-6 bg-[rgba(244,200,74,0.06)] border-[rgba(244,200,74,0.22)]">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="text-sm text-[color:var(--alb-text-muted)] flex items-center gap-1"><Wallet className="h-4 w-4" /> Cartera</div>
                            <div data-testid="creator-wallet-balance" className="font-display text-3xl font-bold font-mono-tab mt-1">{wallet.balance.toFixed(4)} {wallet.currency}</div>
                            <div className="text-xs text-[color:var(--alb-text-muted)] mt-1">Taxa: {wallet.rate_per_view} {wallet.currency}/visualització · Mínim retirada: {wallet.min_withdrawal} {wallet.currency}</div>
                        </div>
                        <form onSubmit={submitWithdraw} className="flex items-end gap-2">
                            <div>
                                <Label htmlFor="amt">Quantitat a retirar</Label>
                                <Input data-testid="withdraw-amount-input" id="amt" type="number" step="0.01" min={wallet.min_withdrawal} value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)] w-40 font-mono-tab" />
                            </div>
                            <Button data-testid="creator-wallet-withdraw-button" type="submit" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]"><ArrowDownToLine className="h-4 w-4 mr-1" /> Sol·licitar retirada</Button>
                        </form>
                    </div>
                    <div className="mt-4 border-t border-[rgba(244,200,74,0.18)] pt-4" data-testid="stripe-connect-section">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <div className="text-sm font-semibold">Stripe Connect</div>
                                <p className="max-w-xl text-xs text-[color:var(--alb-text-muted)]">Connecta el teu compte Stripe per rebre pagaments i perquè les retirades aprovades es transfereixin automàticament.</p>
                            </div>
                            {connectStatus?.connected && connectStatus.details_submitted ? (
                                <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(120,220,160,0.4)] bg-[rgba(120,220,160,0.1)] px-3 py-1 text-xs"><ShieldCheck className="h-3.5 w-3.5" /> Connectat{connectStatus.payouts_enabled ? ' · pagaments actius' : ''}</span>
                            ) : (
                                <Button type="button" disabled={connectBusy} onClick={connectStripe} data-testid="stripe-connect-button" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]"><CreditCard className="h-4 w-4 mr-1" /> {connectStatus?.connected ? 'Completar el compte Stripe' : 'Connectar Stripe'}</Button>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 border-t border-[rgba(244,200,74,0.18)] pt-4" data-testid="auto-withdraw-section">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-semibold">Retirada automàtica</div>
                                <p className="max-w-xl text-xs text-[color:var(--alb-text-muted)]">Quan el saldo arribi al mínim ({wallet.min_withdrawal} {wallet.currency}) es retirarà tot sol. Si tens Stripe Connect completat, els diners arriben al teu compte sense passar per l{'\u2019'}administració. Tria cada quants dies (opcional).</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {[7, 15, 31].map((days) => (
                                    <Button key={days} type="button" variant="outline" onClick={() => saveAutoWithdraw(true, days)} data-testid={`auto-withdraw-${days}`} className={wallet.auto_withdraw_enabled && wallet.auto_withdraw_interval_days === days ? 'border-[rgba(120,220,160,0.6)] bg-[rgba(120,220,160,0.12)]' : 'border-[rgba(244,200,74,0.4)]'}>
                                        Cada {days} dies
                                    </Button>
                                ))}
                                {wallet.auto_withdraw_enabled && (
                                    <Button type="button" variant="ghost" onClick={() => saveAutoWithdraw(false, wallet.auto_withdraw_interval_days || 7)} data-testid="auto-withdraw-off">Desactivar</Button>
                                )}
                            </div>
                        </div>
                        {wallet.auto_withdraw_enabled && wallet.next_auto_withdraw_at && (
                            <div className="mt-2 text-xs text-[color:var(--alb-text-muted)]">Propera comprovació: {new Date(wallet.next_auto_withdraw_at).toLocaleDateString('ca')} · cada {wallet.auto_withdraw_interval_days} dies</div>
                        )}
                    </div>
                    <div className="mt-4 border-t border-[rgba(244,200,74,0.18)] pt-4" data-testid="money-pin-section">
                        <div className="text-sm font-semibold">PIN de seguretat per enviar diners</div>
                        <p className="max-w-xl text-xs text-[color:var(--alb-text-muted)]">Defineix un PIN de 6 xifres. Quan enviïs diners a una amistat, a més de la contrasenya (demanada dues vegades), hauràs d’indicar 3 xifres del PIN triades a l’atzar.</p>
                        <form onSubmit={saveMoneyPin} className="mt-2 flex flex-wrap items-end gap-2">
                            <div>
                                <Label className="text-xs">PIN (6 xifres)</Label>
                                <Input type="password" autoComplete="new-password" data-testid="money-pin-input" value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" className="w-28 bg-[color:var(--alb-ink-2)] font-mono-tab" />
                            </div>
                            <div>
                                <Label className="text-xs">Repeteix el PIN</Label>
                                <Input type="password" autoComplete="new-password" data-testid="money-pin-input-2" value={pinValue2} onChange={(e) => setPinValue2(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" className="w-28 bg-[color:var(--alb-ink-2)] font-mono-tab" />
                            </div>
                            <div>
                                <Label className="text-xs">Contrasenya</Label>
                                <Input type="password" data-testid="money-pin-password" value={pinPassword} onChange={(e) => setPinPassword(e.target.value)} className="w-40 bg-[color:var(--alb-ink-2)]" />
                            </div>
                            <Button type="submit" data-testid="money-pin-save" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Desar PIN</Button>
                        </form>
                    </div>
                    <div className="mt-4 border-t border-[rgba(244,200,74,0.18)] pt-4" data-testid="earnings-csv-section">
                        <div className="text-sm font-semibold">Fiscalitat i declaració d'ingressos</div>
                        <p className="max-w-xl text-xs text-[color:var(--alb-text-muted)]">Descarrega un CSV amb tots els teus moviments (vendes, retirades i transferències) amb la comissió de la plataforma aplicada, perquè puguis presentar els teus ingressos fàcilment.</p>
                        <Button type="button" onClick={downloadEarningsCsv} variant="outline" className="mt-2 border-[rgba(244,200,74,0.4)]" data-testid="earnings-csv-button">Descarregar CSV dels meus ingressos</Button>
                    </div>
                    {walletPacks.length > 0 && (
                        <div className="mt-4 border-t border-[rgba(244,200,74,0.18)] pt-4">
                            <div className="text-sm font-semibold mb-1">Recarregar cartera</div>
                            <p className="text-xs text-[color:var(--alb-text-muted)] mb-2">Compra un pack amb Stripe per tenir saldo per a les teves promocions.</p>
                            <div className="flex flex-wrap gap-2">
                                {walletPacks.map((pack) => (
                                    <Button key={pack.index} type="button" disabled={topupBusy} onClick={() => buyWalletPack(pack)} data-testid={`wallet-pack-${pack.index}`} variant="outline" className="border-[rgba(244,200,74,0.4)]">
                                        <CreditCard className="h-4 w-4 mr-1" /> {pack.credit} {pack.currency} · {pack.price.toFixed(2)} €
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                    {wallet.withdrawals?.length > 0 && (
                        <div className="mt-4">
                            <div className="text-sm font-semibold mb-2">Històric de sol·licituds</div>
                            <div className="text-xs space-y-1">
                                {wallet.withdrawals.slice(0, 5).map((w) => (
                                    <div key={w.withdrawal_id} className="flex justify-between gap-2 text-[color:var(--alb-text-muted)]">
                                        <span className="font-mono-tab">{w.amount.toFixed(2)} {wallet.currency}</span>
                                        <span className="capitalize">{w.status}</span>
                                        <span>{new Date(w.created_at).toLocaleDateString('ca')}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            <Card className="p-5 mb-6 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="received-comments-card">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h2 className="flex items-center gap-2 font-display font-semibold"><MessageCircle className="h-5 w-5 text-[color:var(--alb-aqua)]" /> Comentaris dels teus vídeos</h2>
                        <p className="mt-1 text-sm text-[color:var(--alb-text-muted)]">Llegeix els comentaris i respon directament als espectadors.</p>
                    </div>
                    <span className="text-sm font-mono-tab text-[color:var(--alb-text-muted)]">{receivedComments.length}</span>
                </div>
                <div className="mt-4 space-y-3">
                    {receivedComments.map((item) => (
                        <div key={item.comment_id} className="rounded-lg border border-[color:var(--alb-hairline)] p-3">
                            <div className="flex items-start gap-3">
                                <Avatar className="h-9 w-9"><AvatarImage src={item.user_picture} /><AvatarFallback>{(item.user_name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2 text-sm"><strong>{item.user_name}</strong><span className="text-xs text-[color:var(--alb-text-muted)]">a {item.video_title}</span></div>
                                    <p className="mt-1 text-sm whitespace-pre-wrap">{item.content}</p>
                                    {(item.replies || []).map((reply) => <div key={reply.comment_id} className="mt-2 border-l-2 border-[color:var(--alb-aqua)] pl-3 text-sm"><strong>{reply.user_name}</strong><p className="whitespace-pre-wrap">{reply.content}</p></div>)}
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <Input value={replyDrafts[item.comment_id] || ''} onChange={(event) => setReplyDrafts((drafts) => ({ ...drafts, [item.comment_id]: event.target.value }))} placeholder="Escriu una resposta..." className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                        <Button type="button" onClick={() => replyToComment(item.comment_id)} className="bg-[color:var(--alb-aqua)] text-[color:var(--alb-ink)]">Respondre</Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => toggleBlockedUser({ user_id: item.user_id, name: item.user_name, picture: item.user_picture })}>{blockedUsers.some((person) => person.user_id === item.user_id) ? 'Desbloquejar' : 'Bloquejar usuari'}</Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => toggleCommentPermission(item)}>{(mine.find((video) => video.video_id === item.video_id)?.comment_allowed_user_ids || []).includes(item.user_id) ? 'Retirar permís' : 'Permetre només a aquest usuari'}</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {receivedComments.length === 0 && <p className="text-sm text-[color:var(--alb-text-muted)]">Encara no has rebut comentaris.</p>}
                </div>
                {blockedUsers.length > 0 && <div className="mt-5 border-t border-[color:var(--alb-hairline)] pt-4"><h3 className="text-sm font-semibold">Usuaris bloquejats ({blockedUsers.length})</h3><div className="mt-2 flex flex-wrap gap-2">{blockedUsers.map((person) => <Button key={person.user_id} size="sm" variant="outline" onClick={() => toggleBlockedUser(person)}>{person.name} · Desbloquejar</Button>)}</div></div>}
            </Card>

            <Card className="p-5 mb-6 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="user-videos-always-visible">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h2 className="font-display font-semibold">Els meus vídeos</h2>
                        <p className="mt-1 text-sm text-[color:var(--alb-text-muted)]">El teu contingut és visible sempre al perfil.</p>
                    </div>
                    <span className="text-sm font-mono-tab text-[color:var(--alb-text-muted)]">{mine.length}</span>
                </div>
                {mine.length === 0 ? (
                    <p className="mt-4 text-sm text-[color:var(--alb-text-muted)]">Encara no has pujat cap vídeo.</p>
                ) : (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {mine.map((v) => (
                            <div key={v.video_id} className="relative">
                                <VideoCard video={v} />
                                <button onClick={() => deleteMyVideo(v.video_id)} data-testid={`delete-video-${v.video_id}`} className="absolute top-2 right-2 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/60 hover:bg-[color:var(--alb-pink)] text-white"><Trash2 className="h-4 w-4" /></button>
                                <button onClick={() => setEditingVideo({ ...v, price: v.price || 0.99, categories: (v.categories && v.categories.length ? v.categories : [v.category]) })} data-testid={`edit-video-${v.video_id}`} aria-label={`Editar ${v.title}`} className="absolute top-2 right-12 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/60 hover:bg-[color:var(--alb-aqua)] text-white"><Pencil className="h-4 w-4" /></button>
                                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-[color:var(--alb-hairline)] bg-[color:var(--alb-ink-2)] px-3 py-2 text-xs text-[color:var(--alb-text-muted)]" data-testid={`video-watchstats-${v.video_id}`}>
                                    <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> {formatWatchTime(v.watch_seconds)} vistos</span>
                                    <span className="font-mono-tab">Retenció {(v.retention_percent || 0)}%</span>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] px-3 py-2">
                                    <Label htmlFor={`comments-always-${v.video_id}`} className="text-xs">Permetre comentaris</Label>
                                    <Switch id={`comments-always-${v.video_id}`} checked={v.comments_enabled !== false} onCheckedChange={(enabled) => setCommentsEnabled(v.video_id, enabled)} data-testid={`video-comments-toggle-${v.video_id}`} />
                                </div>
                                <Button type="button" disabled={v.is_adult} onClick={() => promoteMyVideo(v)} data-testid={`promote-video-${v.video_id}`} title={v.is_adult ? 'El contingut +18 no es pot promocionar' : 'Promocionar aquest vídeo'} className="mt-2 w-full bg-[color:var(--alb-aqua)] text-[color:var(--alb-ink)] hover:opacity-90"><Megaphone className="h-4 w-4 mr-1" /> Promocionar</Button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab} id="profile-tabs">
                <TabsList className="bg-[color:var(--alb-surface-2)]">
                    <TabsTrigger value="analytics" data-testid="profile-tab-analytics">Analítica</TabsTrigger>
                    <TabsTrigger value="social" data-testid="profile-tab-social">Comunitat ({social.followers.length})</TabsTrigger>
                    <TabsTrigger value="history" data-testid="profile-tab-history">Historial</TabsTrigger>
                    <TabsTrigger value="recos" data-testid="profile-tab-recos">Recomanacions</TabsTrigger>
                    <TabsTrigger value="promo" data-testid="profile-tab-promo">Promoció</TabsTrigger>
                </TabsList>
                <TabsContent value="social" className="mt-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        {[
                            { title: 'Qui et segueix', items: social.followers },
                            { title: 'Creadors que segueixes', items: social.following },
                        ].map((group) => (
                            <Card key={group.title} className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <h2 className="font-display font-semibold">{group.title} ({group.items.length})</h2>
                                <div className="mt-4 space-y-3">
                                    {group.items.map((person) => (
                                        <Link key={person.user_id} to={`/usuari/${person.user_id}`} className="flex items-center gap-3 rounded border border-[color:var(--alb-hairline)] p-3 hover:border-[color:var(--alb-aqua)]">
                                            <Avatar className="h-10 w-10"><AvatarImage src={person.picture} /><AvatarFallback>{(person.name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                                            <div className="min-w-0"><div className="truncate font-medium">{person.name}</div>{person.bio && <div className="truncate text-xs text-[color:var(--alb-text-muted)]">{person.bio}</div>}</div>
                                        </Link>
                                    ))}
                                    {group.items.length === 0 && <p className="text-sm text-[color:var(--alb-text-muted)]">Encara no hi ha ningú en aquesta llista.</p>}
                                </div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="analytics" className="mt-4">
                    {analytics && (
                        <div className="space-y-5" data-testid="creator-analytics">
                            <div className="flex flex-wrap items-center justify-between gap-3"><div><Label htmlFor="creator-analytics-days">Període</Label><Select value={String(analyticsDays)} onValueChange={(value) => setAnalyticsDays(Number(value))}><SelectTrigger id="creator-analytics-days" className="mt-1 w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="30">30 dies</SelectItem><SelectItem value="90">90 dies</SelectItem><SelectItem value="180">180 dies</SelectItem><SelectItem value="365">1 any</SelectItem></SelectContent></Select></div><Button type="button" variant="outline" onClick={downloadCreatorAnalytics}><Download className="mr-2 h-4 w-4" /> Baixar CSV</Button></div>
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                                {[
                                    { label: 'Visualitzacions', value: analytics.summary.views, icon: Eye },
                                    { label: 'Espectadors únics', value: analytics.summary.unique_viewers, icon: Users },
                                    { label: "M'agrades", value: analytics.summary.likes, icon: Heart },
                                    { label: 'Comentaris', value: analytics.summary.comments, icon: MessageCircle },
                                    { label: 'Guanys per visualitzacions', value: `${Number(analytics.summary.view_revenue || 0).toFixed(4)} ${wallet?.currency || 'EUR'}`, icon: Eye },
                                    { label: 'Guanys per vendes', value: `${Number(analytics.summary.sales_revenue || 0).toFixed(4)} ${wallet?.currency || 'EUR'}`, icon: CreditCard },
                                ].map(({ label, value, icon: Icon }) => (
                                    <Card key={label} className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                        <Icon className="h-4 w-4 text-[color:var(--alb-aqua)]" />
                                        <div className="mt-3 font-display text-2xl font-semibold font-mono-tab">{value}</div>
                                        <div className="mt-1 text-xs text-[color:var(--alb-text-muted)]">{label} · {analytics.period_days} dies</div>
                                    </Card>
                                ))}
                            </div>

                            <Card className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <h2 className="flex items-center gap-2 font-display font-semibold"><BarChart3 className="h-4 w-4 text-[color:var(--alb-sun)]" /> Evolució de visualitzacions</h2>
                                <div className="mt-5 flex h-40 items-end gap-1" aria-label="Visualitzacions diàries dels últims 30 dies">
                                    {analytics.daily.map((day) => {
                                        const maxViews = Math.max(...analytics.daily.map((item) => item.views), 1);
                                        const height = day.views === 0 ? 3 : Math.max((day.views / maxViews) * 100, 8);
                                        return (
                                            <div key={day.date} className="group relative flex min-w-0 flex-1 items-end h-full">
                                                <div className="w-full rounded-t-sm bg-[color:var(--alb-aqua)] opacity-80" style={{ height: `${height}%` }} />
                                                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white group-hover:block">{day.date}: {day.views}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>

                            <Card className="overflow-hidden bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <div className="p-5"><h2 className="font-display font-semibold">Vídeos amb millor rendiment</h2></div>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[560px] text-sm">
                                        <thead className="border-y border-[color:var(--alb-hairline)] text-left text-xs text-[color:var(--alb-text-muted)]"><tr><th className="px-5 py-3">Vídeo</th><th className="px-3 py-3">Visualitzacions</th><th className="px-3 py-3">M'agrades</th><th className="px-3 py-3">Comentaris</th></tr></thead>
                                        <tbody>{analytics.videos.map((video) => <tr key={video.video_id} className="border-b border-[color:var(--alb-hairline)] last:border-0"><td className="px-5 py-3"><Link to={`/watch/${video.video_id}`} className="font-medium hover:text-[color:var(--alb-sun)]">{video.title}</Link></td><td className="px-3 py-3 font-mono-tab">{video.views}</td><td className="px-3 py-3 font-mono-tab">{video.likes}</td><td className="px-3 py-3 font-mono-tab">{video.comments}</td></tr>)}</tbody>
                                    </table>
                                </div>
                            </Card>

                            <Card className="overflow-hidden bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <div className="p-5">
                                    <h2 className="font-display font-semibold">Rànquing de vendes</h2>
                                    <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Ordenat pel nombre de compres. El net és l’import que rep el creador després de la comissió.</p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[720px] text-sm">
                                        <thead className="border-y border-[color:var(--alb-hairline)] text-left text-xs text-[color:var(--alb-text-muted)]"><tr><th className="px-5 py-3">Posició</th><th className="px-3 py-3">Vídeo</th><th className="px-3 py-3">Compres</th><th className="px-3 py-3">Brut</th><th className="px-3 py-3">Comissió</th><th className="px-3 py-3">Net</th></tr></thead>
                                        <tbody>
                                            {(analytics.sales_ranking || []).map((video, index) => (
                                                <tr key={video.video_id} className="border-b border-[color:var(--alb-hairline)] last:border-0">
                                                    <td className="px-5 py-3 font-mono-tab">{index + 1}</td>
                                                    <td className="px-3 py-3"><Link to={`/watch/${video.video_id}`} className="font-medium hover:text-[color:var(--alb-sun)]">{video.title}</Link></td>
                                                    <td className="px-3 py-3 font-mono-tab">{video.purchases}</td>
                                                    <td className="px-3 py-3 font-mono-tab">{video.gross_revenue.toFixed(2)} {wallet?.currency || 'EUR'}</td>
                                                    <td className="px-3 py-3 font-mono-tab">{video.platform_fee.toFixed(2)} {wallet?.currency || 'EUR'}</td>
                                                    <td className="px-3 py-3 font-mono-tab text-[color:var(--alb-aqua)]">{video.creator_revenue.toFixed(2)} {wallet?.currency || 'EUR'}</td>
                                                </tr>
                                            ))}
                                            {(analytics.sales_ranking || []).length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[color:var(--alb-text-muted)]">Encara no hi ha vídeos de pagament.</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="history" className="mt-4">
                    {history.length === 0 ? (
                        <div className="text-[color:var(--alb-text-muted)] text-sm">Encara no has vist cap vídeo.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {history.map((v) => <VideoCard key={v.video_id + v.watched_at} video={v} />)}
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="recos" className="mt-4">
                    {recos.length === 0 ? (
                        <div className="text-[color:var(--alb-text-muted)] text-sm">Mira alguns vídeos per rebre recomanacions.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {recos.map((v) => <VideoCard key={v.video_id} video={v} />)}
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="promo" className="mt-4" data-testid="profile-promo-tab">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                            <h2 className="font-display font-semibold">Promociona el teu contingut</h2>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">
                                Paga amb el saldo de la cartera ({wallet ? wallet.balance.toFixed(2) : '…'} {promoConfig?.currency || 'EUR'}) i arriba a més gent.
                                Cost: {promoConfig?.cost_per_visit ?? '…'} {promoConfig?.currency || 'EUR'} per visualització · Mínim {promoConfig?.min_views ?? '…'} visualitzacions.
                                El contingut +18 no es pot promocionar.
                            </p>
                            {promoConfig?.promo_enabled === false ? (
                                <p className="mt-4 text-sm text-[color:var(--alb-text-muted)]">Les promocions estan desactivades temporalment.</p>
                            ) : (
                                <form onSubmit={createPromoAd} className="mt-4 space-y-3">
                                    <div>
                                        <Label>Què vols promocionar?</Label>
                                        <Select value={promoForm.kind} onValueChange={(kind) => setPromoForm({ ...promoForm, kind })}>
                                            <SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="video">Un vídeo meu</SelectItem>
                                                <SelectItem value="series">Una sèrie meva</SelectItem>
                                                <SelectItem value="external">Una pàgina web externa</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {promoForm.kind === 'video' && (
                                        <div>
                                            <Label>Vídeo</Label>
                                            <Select value={promoForm.target_video_id} onValueChange={(target_video_id) => setPromoForm({ ...promoForm, target_video_id })}>
                                                <SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue placeholder="Tria un vídeo" /></SelectTrigger>
                                                <SelectContent>
                                                    {mine.filter((video) => video.status === 'published' && !video.is_adult).map((video) => (
                                                        <SelectItem key={video.video_id} value={video.video_id}>{video.title}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    {promoForm.kind === 'series' && (
                                        <div>
                                            <Label>Sèrie</Label>
                                            <Select value={promoForm.target_series_id} onValueChange={(target_series_id) => setPromoForm({ ...promoForm, target_series_id })}>
                                                <SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue placeholder="Tria una sèrie" /></SelectTrigger>
                                                <SelectContent>
                                                    {mySeries.filter((serie) => !serie.is_adult).map((serie) => (
                                                        <SelectItem key={serie.series_id} value={serie.series_id}>{serie.title}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    {promoForm.kind === 'external' && (
                                        <>
                                            <div><Label>URL de la pàgina</Label><Input value={promoForm.target_url} onChange={(event) => setPromoForm({ ...promoForm, target_url: event.target.value })} placeholder="https://..." className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                            <div><Label>Títol de l'anunci</Label><Input value={promoForm.title} onChange={(event) => setPromoForm({ ...promoForm, title: event.target.value })} maxLength={120} placeholder="El text que es mostrarà" className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                            <div>
                                                <Label>Bàner de l'anunci (mida estàndard)</Label>
                                                <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => onPromoBannerFile(event.target.files?.[0] || null)} data-testid="promo-banner-input" className="mt-1 bg-[color:var(--alb-ink-2)]" />
                                                <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">
                                                    {promoBannerFile ? `Seleccionat: ${promoBannerFile.name}` : `Mides acceptades: ${STANDARD_BANNER_SIZES.map(([w, h]) => `${w}x${h}`).join(', ')}. Màxim 2 MB.`}
                                                </p>
                                            </div>
                                        </>
                                    )}
                                    <div>
                                        <Label>On es mostrarà?</Label>
                                        <Select value={promoForm.placement} onValueChange={(placement) => setPromoForm({ ...promoForm, placement })}>
                                            <SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="banner">Bàner rotatiu (portada i pàgines)</SelectItem>
                                                <SelectItem value="preroll">Abans dels vídeos gratuïts</SelectItem>
                                                <SelectItem value="postroll">Després dels vídeos gratuïts</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Visualitzacions a contractar (mínim {promoConfig?.min_views || 100})</Label>
                                        <Input type="number" min={promoConfig?.min_views || 100} step={promoConfig?.min_views || 100} value={promoForm.views} onChange={(event) => setPromoForm({ ...promoForm, views: event.target.value })} data-testid="promo-views-input" className="mt-1 font-mono-tab bg-[color:var(--alb-ink-2)]" />
                                        <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">
                                            Preu: {((parseInt(promoForm.views || '0', 10) || 0) * (promoConfig?.cost_per_visit || 0)).toFixed(2)} {promoConfig?.currency || 'EUR'} · Es descompta de la cartera en crear la promoció.
                                        </p>
                                    </div>
                                    <Button type="submit" disabled={promoBusy} className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" data-testid="promo-create-button">Comprar i activar la promoció</Button>
                                </form>
                            )}
                        </Card>
                        <Card className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                            <h2 className="font-display font-semibold">Les meves promocions</h2>
                            <div className="mt-4 space-y-3" data-testid="promo-ads-list">
                                {promoAds.length === 0 && <p className="text-sm text-[color:var(--alb-text-muted)]">Encara no tens cap promoció.</p>}
                                {promoAds.map((ad) => (
                                    <div key={ad.ad_id} className="rounded border border-[color:var(--alb-hairline)] p-3" data-testid={`promo-ad-${ad.ad_id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium">{ad.title}</div>
                                                <div className="text-xs text-[color:var(--alb-text-muted)]">
                                                    {ad.placement === 'banner' ? 'Bàner rotatiu' : ad.placement === 'preroll' ? 'Abans dels vídeos gratuïts' : 'Després dels vídeos gratuïts'}
                                                    {' · '}<span className="font-mono-tab">{ad.views}/{ad.budget_views}</span> visualitzacions · <span className="font-mono-tab">{ad.clicks}</span> clics
                                                </div>
                                                <div className="mt-1 h-1.5 w-full max-w-[220px] rounded bg-[color:var(--alb-surface-2)]">
                                                    <div className="h-1.5 rounded bg-[color:var(--alb-sun)]" style={{ width: `${Math.min(100, (ad.views / ad.budget_views) * 100)}%` }} />
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 gap-1">
                                                {!ad.finished && <Button size="sm" variant="outline" onClick={() => togglePromoPause(ad)}>{ad.paused ? 'Reprendre' : 'Pausar'}</Button>}
                                                <Button size="sm" variant="destructive" onClick={() => deletePromoAd(ad)}><Trash2 className="h-3 w-3" /></Button>
                                            </div>
                                        </div>
                                        {ad.kind === 'external' && (
                                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                {ad.banner ? <span className="inline-flex items-center gap-1 text-[color:var(--alb-text-muted)]"><ShieldCheck className="h-3.5 w-3.5" /> Bàner actiu</span> : <span className="text-[color:var(--alb-text-muted)]">Sense bàner</span>}
                                                <label className="inline-flex cursor-pointer items-center rounded border border-[color:var(--alb-hairline)] px-2 py-1 hover:bg-[color:var(--alb-surface-2)]" data-testid={`promo-banner-change-${ad.ad_id}`}>
                                                    {ad.banner ? 'Canviar bàner' : 'Pujar bàner'}
                                                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (!file) return; validateBannerSize(file, () => uploadPromoBanner(ad, file)); }} />
                                                </label>
                                            </div>
                                        )}
                                        {(ad.paused || ad.finished) && <div className="mt-2 text-xs text-[color:var(--alb-text-muted)]">{ad.finished ? 'Completada: ja ha arribat a les visualitzacions contractades.' : 'Pausada: no es mostra fins que la reprenguis.'}</div>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={editingProfile} onOpenChange={setEditingProfile}>
                <DialogContent className="bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                    <DialogHeader><DialogTitle>Editar perfil</DialogTitle></DialogHeader>
                    <form onSubmit={saveProfile} className="space-y-4">
                        <div><Label htmlFor="profile-name-input">Nom públic</Label><Input id="profile-name-input" required maxLength={80} value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                        <div><Label htmlFor="profile-bio-input">Sobre tu</Label><Textarea id="profile-bio-input" maxLength={500} rows={5} value={profileForm.bio} onChange={(event) => setProfileForm({ ...profileForm, bio: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /><p className="mt-1 text-right text-xs text-[color:var(--alb-text-muted)]">{profileForm.bio.length}/500</p></div>
                        <div className="rounded border border-[color:var(--alb-hairline)] p-3">
                            <Label>Xarxes socials (opcional)</Label>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Si omples algun camp, tothom veurà els enllaços al teu perfil públic. Deixa'ls buits per no mostrar-ne cap.</p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                <div><Label htmlFor="profile-instagram-input" className="text-xs">Instagram</Label><Input id="profile-instagram-input" maxLength={200} placeholder="@usuari o URL" value={profileForm.instagram} onChange={(event) => setProfileForm({ ...profileForm, instagram: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                <div><Label htmlFor="profile-tiktok-input" className="text-xs">TikTok</Label><Input id="profile-tiktok-input" maxLength={200} placeholder="@usuari o URL" value={profileForm.tiktok} onChange={(event) => setProfileForm({ ...profileForm, tiktok: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                <div><Label htmlFor="profile-twitter-input" className="text-xs">X / Twitter</Label><Input id="profile-twitter-input" maxLength={200} placeholder="@usuari o URL" value={profileForm.twitter} onChange={(event) => setProfileForm({ ...profileForm, twitter: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                                <div><Label htmlFor="profile-youtube-input" className="text-xs">YouTube</Label><Input id="profile-youtube-input" maxLength={200} placeholder="URL del canal" value={profileForm.youtube} onChange={(event) => setProfileForm({ ...profileForm, youtube: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                            </div>
                        </div>
                        <div><Label htmlFor="profile-picture-input">Foto de perfil</Label><Input id="profile-picture-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setProfileForm({ ...profileForm, picture: event.target.files?.[0] || null })} className="mt-1 bg-[color:var(--alb-ink-2)]" /><p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">JPG, PNG o WebP. Màxim 5 MB.</p></div>
                        <Button type="submit" className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Desar perfil</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={Boolean(editingVideo)} onOpenChange={(open) => !open && setEditingVideo(null)}>
                <DialogContent className="max-h-[90vh] overflow-y-auto bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                    <DialogHeader><DialogTitle>Editar vídeo</DialogTitle></DialogHeader>
                    {editingVideo && (
                        <form onSubmit={saveVideo} className="space-y-4">
                            <div><Label htmlFor="edit-video-title">Títol</Label><Input id="edit-video-title" value={editingVideo.title} maxLength={140} onChange={(event) => setEditingVideo({ ...editingVideo, title: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                            <div><Label htmlFor="edit-video-description">Descripció</Label><Textarea id="edit-video-description" value={editingVideo.description || ''} rows={5} onChange={(event) => setEditingVideo({ ...editingVideo, description: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /></div>
                            <div>
                                <Label>Categories (tria una o més; la primera és la principal)</Label>
                                <div className="mt-1 flex flex-wrap gap-2" data-testid="edit-video-categories">
                                    {categories.map((c) => {
                                        const selected = (editingVideo.categories || []).includes(c);
                                        return (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => setEditingVideo((prev) => {
                                                    const has = (prev.categories || []).includes(c);
                                                    const next = has ? (prev.categories || []).filter((x) => x !== c) : [...(prev.categories || []), c];
                                                    if (!next.length) return prev; // sempre almenys una categoria
                                                    return { ...prev, categories: next, category: next[0] };
                                                })}
                                                className={`px-3 py-1.5 rounded-full text-xs border transition ${selected ? 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] border-[color:var(--alb-sun)]' : 'border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface)]'}`}
                                            >
                                                {translateCategory(c, locale)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div><Label>Visibilitat</Label><Select value={editingVideo.status} onValueChange={(status) => setEditingVideo({ ...editingVideo, status })}><SelectTrigger className="mt-1 bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="published">Publicat</SelectItem><SelectItem value="hidden">Ocult</SelectItem></SelectContent></Select></div>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">Short <Switch checked={editingVideo.is_short} onCheckedChange={(is_short) => setEditingVideo({ ...editingVideo, is_short })} /></Label>
                                <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">Vídeo de pagament individual <Switch checked={editingVideo.is_premium} onCheckedChange={(is_premium) => setEditingVideo({ ...editingVideo, is_premium })} /></Label>
                                <Label className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] p-3">Comentaris <Switch checked={editingVideo.comments_enabled !== false} onCheckedChange={(comments_enabled) => setEditingVideo({ ...editingVideo, comments_enabled })} /></Label>
                            </div>
                            <div>
                                <Label>Subtítols (tria l’idioma i puja un fitxer o escriu-ne el text)</Label>
                                {(editingVideo.subtitles || []).length > 0 && (
                                    <ul className="mt-1 space-y-1" data-testid="edit-video-subtitles">
                                        {(editingVideo.subtitles || []).map((s) => (
                                            <li key={s.lang} className="flex items-center justify-between gap-2 rounded border border-[color:var(--alb-hairline)] px-3 py-1.5 text-sm">
                                                <span>{s.name} <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--alb-text-muted)] underline">veure</a></span>
                                                <button type="button" onClick={() => deleteSubtitle(s.lang)} className="text-red-400 hover:text-red-300" aria-label={`Eliminar subtítols en ${s.name}`}><Trash2 className="h-4 w-4" /></button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-2 grid gap-2 sm:grid-cols-[180px_1fr_auto] items-center">
                                    <Select value={subLang} onValueChange={setSubLang}>
                                        <SelectTrigger className="bg-[color:var(--alb-ink-2)]"><SelectValue /></SelectTrigger>
                                        <SelectContent>{subtitleLangs.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                    {subMode === 'file' ? (
                                        <Input type="file" accept=".srt,.vtt" onChange={(event) => setSubFile(event.target.files?.[0] || null)} className="bg-[color:var(--alb-ink-2)]" data-testid="subtitle-file-input" />
                                    ) : (
                                        <Textarea rows={3} placeholder={'WEBVTT\n\n00:00:01.000 --> 00:00:04.000\nHola a tothom!'} value={subText} onChange={(event) => setSubText(event.target.value)} className="bg-[color:var(--alb-ink-2)]" />
                                    )}
                                    <Button type="button" disabled={subBusy} onClick={uploadSubtitle} data-testid="subtitle-upload" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">{subBusy ? 'Desant…' : 'Afegir'}</Button>
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-xs text-[color:var(--alb-text-muted)]">
                                    <button type="button" onClick={() => setSubMode('file')} className={subMode === 'file' ? 'underline text-white' : 'hover:text-white'}>Pujar fitxer SRT/VTT</button>
                                    <button type="button" onClick={() => setSubMode('text')} className={subMode === 'text' ? 'underline text-white' : 'hover:text-white'}>Escriure el text</button>
                                </div>
                            </div>
                            {editingVideo.is_premium && <div><Label htmlFor="edit-video-price">Preu per veure aquest vídeo (EUR)</Label><Input id="edit-video-price" type="number" min="0.5" step="0.01" required value={editingVideo.price} onChange={(event) => setEditingVideo({ ...editingVideo, price: event.target.value })} className="mt-1 bg-[color:var(--alb-ink-2)]" /><p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">L’espectador compra accés permanent només a aquest vídeo.</p></div>}
                            <Button type="submit" data-testid="save-video-edit" className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Desar canvis</Button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </main>
    );
}
